import LinkViewModel from '@/ViewModels/Controls/LinkViewModel';
import ImageViewModel from '@/ViewModels/Controls/ImageViewModel';
import ImageViewModelBuilder from '@/Builder/ImageViewModelBuilder';
import LinkViewModelBuilder from '@/Builder/LinkViewModelBuilder';
import NavigationHelper from '@/Management/NavigationHelper';
import ComponentManager from '@/Management/ComponentManager';
import * as DateHelper from '@/Utility/DateHelper';
import ControlViewModel from '@/ViewModels/ControlViewModel';
import { IActivityFeedItemConstructorParameter } from '#/DefaultViewExtensionInterfaces';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';
import LocatorHelper from '@/Utility/LocatorHelper';
import { IActivityFeedItemDTO } from "coreframetypings/dto";
import RADApplication from "@/Management/RADApplication";

export default class ActivityFeedItemViewModel extends ControlViewModel{

    public objectIdentValue: string;
    public objectPdoId: string;
    public objectType: string;
    public objectTypeName: string;
    public principal?: string;
    public timeStamp: string;
    public type: string;
    public typeImage: ImageViewModel;
    public objectLink: LinkViewModel;
    public user?: string;
    public optionsImage: ImageViewModel;
    public loadMoreLink: LinkViewModel;
    public showLoadMoreLink: KnockoutObservable<boolean>;
    public moreDataLoaded: KnockoutObservable<boolean>;

    public objectTypeLabel: LabelViewModel;
    public objectTimestampLabel: LabelViewModel;
    public objectCreatedByLabel: LabelViewModel;
    public objectPrincipalLabel: LabelViewModel;

    public timestampHeadlineLabel: LabelViewModel;
    public createdByHeadlineLabel: LabelViewModel;
    public objectPrincipalHeadlineLabel: LabelViewModel;

    public itemIndex: number;
    public itemUnit: string;

    constructor(params: IActivityFeedItemConstructorParameter) {
        super(params)

        const activity = params.activityItemDTO;

        this.objectIdentValue = activity.objectIdentValue;
        this.objectPdoId = activity.objectPdoId;
        this.objectType = activity.objectType;
        this.objectTypeName = activity.objectTypeName;
        this.principal = activity.principal;
        this.itemIndex = this.paramOrDefault(params.itemIndex, -1);
        this.itemUnit = this.paramOrDefault(params.unit, null);
        this.timeStamp = DateHelper.toString(DateHelper.parseDotNetDateString(activity.timeStamp), true, params.unit !== 'Today');
        this.type = activity.type;
        this.typeImage = this.getImage(activity.type)
        this.user = activity.user;
        this.objectLink = this.getLink(activity);

        this.objectTypeLabel = this.buildObjectTypeLabel();
        this.objectTimestampLabel = this.buildObjectTimestampLabel();
        this.objectCreatedByLabel = this.buildObjectCreatedByLabel();
        this.objectPrincipalLabel = this.buildObjectPrincipalLabel();

        this.timestampHeadlineLabel = this.buildTimestampHeadlineLabel();
        this.createdByHeadlineLabel = this.buildCreatedByHeadlineLabel();
        this.objectPrincipalHeadlineLabel = this.buildObjectPrincipalHeadlineLabel();
    }

    private getImage(itemType: string): ImageViewModel {
        const builder = new ImageViewModelBuilder();
        if (itemType === "Created") {
            builder.withSource("add.svg");
        } else if (itemType === "Changed") {
            builder.withSource("pencil.svg");
        } else {
            throw new Error(`Item type '${itemType}' not supported`);
        }
        return builder.build();
    }

    private buildObjectTypeLabel(): LabelViewModel {
        return new LabelViewModelBuilder()
            .withCaption(this.objectType)
            .withLocator(this.getLocator('objectType'))
            .build();
    }

    private buildObjectTimestampLabel(): LabelViewModel {
        return new LabelViewModelBuilder()
            .withCaption(this.timeStamp)
            .withLocator(this.getLocator('timestamp'))
            .build();
    }

    private buildObjectCreatedByLabel(): LabelViewModel {
        return new LabelViewModelBuilder()
            .withCaption(this.user)
            .withLocator(this.getLocator('createdBy'))
            .build();
    }

    private buildObjectPrincipalLabel(): LabelViewModel {
        return new LabelViewModelBuilder()
            .withCaption(this.principal)
            .withLocator(this.getLocator('principal'))
            .build();
    }

    private buildTimestampHeadlineLabel(): LabelViewModel {
        return new LabelViewModelBuilder()
            .withErgName("ACTIVITYFEED_ITEM_LASTEDIT")
            .withLocator(this.getLocator('lastEditDateLabel'))
            .build();
    }

    private buildCreatedByHeadlineLabel(): LabelViewModel {
        return new LabelViewModelBuilder()
            .withErgName('ACTIVITYFEED_ITEM_MODIFIED_BY')
            .withLocator(this.getLocator('modifiedByUserLabel'))
            .build();
    }

    private buildObjectPrincipalHeadlineLabel(): LabelViewModel {
        return new LabelViewModelBuilder()
            .withErgName('ACTIVITYFEED_ITEM_PRINCIPAL')
            .withLocator(this.getLocator('principalLabel'))
            .build();
    }

    private getLocator(type: string): KnockoutObservable<string> {
        return LocatorHelper.buildLocator('activity-feed-item-entry', this.itemUnit, this.itemIndex, type)
    }

    private getLink(item: IActivityFeedItemDTO): LinkViewModel {
        //Der Link soll, sofern das Objekt mit einem Mandanten angelegt wurde, folgenden Aufbau haben: ObjectIdentvalue, [bei MandantenName]
        const principal = RADApplication.getInstance().principal.name();
        const builder = new LinkViewModelBuilder().withOnClick((openInNewWindow) => {
            NavigationHelper.openDetailView("EDIT", item.objectTypeName, {
                pdoId: item.objectPdoId
            }, openInNewWindow);
        }).withLabel().withLocator(this.getLocator('objectLink')).withCaption(item.objectIdentValue);

        if (this.isPrincipalNameVisible(item.principal, principal)) {
            builder.withPostfix()
                .updateProperties({
                    isVisible: this.defineComputed(() => {
                        return this.isPrincipalNameVisible(item.principal, principal)
                    }),
                    ergName: 'ACTIVITYFEED_ITEM_AT_PRINCIPAL',
                }).withSimpleReplacement('principalName', item.principal);
        } 

        return builder.done().build();
    }

    private isPrincipalNameVisible(itemPrincipal: string, currentPrincipal: string): boolean {
        return itemPrincipal !== '' && itemPrincipal !== null && itemPrincipal !== currentPrincipal;
    }
}

ComponentManager.setViewModel('ActivityFeedItem', ActivityFeedItemViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');